/**
 * Backup Manager
 * Phase 2.2: Export/Import UI with linked folder support and nightly backups
 * Professional backup interface with dry-run validation and conflict resolution
 */
import React, { useState, useEffect } from 'react';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from '../../../hooks/useVesselCollections';
import { useLicense } from '../../../hooks/useLicense';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { 
  exportBackup, 
  downloadBackup, 
  saveToLinkedFolder,
  requestBackupDirectory,
  getStoredBackupDirectory,
  cleanupOldBackups,
  supportsFileSystemAccess
} from '../../../services/export';
import { importBackup, createImportSummary } from '../../../services/import';
import { logExport, logImport } from '../../../services/auditTrail';
import { 
  Download,
  Upload,
  Folder,
  Calendar,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

export const BackupManager = () => {
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections, vesselId } = useVesselCollections();
  const { org, hasFeature, FEATURES } = useLicense();
  const { showAlert, showConfirm } = useCustomDialogs();
  
  const [vessels, setVessels] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [linkedFolder, setLinkedFolder] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);

  // Phase 2.6: Backup data loading temporarily disabled - will restore in Phase 3
  // Load vessels and backup settings
  useEffect(() => {
    console.warn('Phase 2.6: Backup data loading disabled - will return in Phase 3');
    setVessels([]);
    setSelectedVessels([]);

    // const loadData = async () => {
    //   try {
    //     // Load vessels
    //     const metaRecords = await collections.list('meta');
    //     const vesselRecords = metaRecords
    //       .filter(record => record.id.startsWith('vessel_'))
    //       .map(record => ({
    //         id: record.id.replace('vessel_', ''),
    //         name: record.data?.name || record.id,
    //         ...record.data
    //       }));
    //
    //     setVessels(vesselRecords);
    //     setSelectedVessels(vesselRecords.map(v => v.id));
    //
    //     // Load backup settings
    //     const backupSettings = metaRecords.find(record => record.id === 'backup_settings');
    //     if (backupSettings?.data) {
    //       setAutoBackupEnabled(backupSettings.data.autoBackupEnabled || false);
    //       setLastBackup(backupSettings.data.lastBackup ? new Date(backupSettings.data.lastBackup) : null);
    //     }
    //
    //     // Load linked folder
    //     if (supportsFileSystemAccess()) {
    //       const storedFolder = await getStoredBackupDirectory();
    //       setLinkedFolder(storedFolder);
    //     }
    //   } catch (error) {
    //
    //   }
    // };
    //
    // loadData();
  }, []);

  // Phase 2.6: Export functionality temporarily disabled - will restore in Phase 3
  const handleExport = async (useLinkedFolder = false) => {
    console.warn('Phase 2.6: Backup export disabled - will return in Phase 3');
    showAlert('Backup export temporarily unavailable during migration');
    return;

    // if (selectedVessels.length === 0) {
    //   showAlert('Please select at least one vessel to export');
    //   return;
    // }
    //
    // setExporting(true);
    // try {
    //   const orgId = org?.id || 'default_org';
    //   const exportData = {
    //     orgId,
    //     vesselIds: selectedVessels,
    //     collections,
    //     org
    //   };
    //
    //   const zipBlob = await exportBackup(exportData);
    //
    //   if (useLinkedFolder && linkedFolder) {
    //     const filename = await saveToLinkedFolder(zipBlob, linkedFolder, org?.name);
    //     showAlert(`Backup saved to linked folder: ${filename}`);
    //
    //     // Clean up old backups
    //     await cleanupOldBackups(linkedFolder);
    //   } else {
    //     downloadBackup(zipBlob, org?.name);
    //     showAlert('Backup downloaded successfully');
    //   }
    //
    //   // Log export
    //   const manifest = {
    //     org: { id: orgId, name: org?.name },
    //     vessels: vessels.filter(v => selectedVessels.includes(v.id)),
    //     exportedAt: new Date().toISOString()
    //   };
    //
    //   await logExport(collections, manifest, {
    //     userId: 'current_user', // TODO: Get from auth context
    //     orgId
    //   });
    //
    //   // Update last backup time
    //   await collections.upsert('meta', {
    //     id: 'backup_settings',
    //     vesselId: 'system',
    //     data: {
    //       autoBackupEnabled,
    //       lastBackup: new Date().toISOString()
    //     },
    //     origin: 'backup_settings'
    //   });
    //
    //   setLastBackup(new Date());
    //
    // } catch (error) {
    //
    //   showAlert(`Export failed: ${error.message}`);
    // } finally {
    //   setExporting(false);
    // }
  };

  // Phase 2.6: Import functionality temporarily disabled - will restore in Phase 3
  const handleFileSelect = async (event) => {
    console.warn('Phase 2.6: Backup import disabled - will return in Phase 3');
    showAlert('Backup import temporarily unavailable during migration');
    return;

    // const file = event.target.files[0];
    // if (!file) return;
    //
    // setImporting(true);
    // try {
    //   // Dry run first
    //   const dryRunResult = await importBackup(file, collections, { dryRun: true });
    //   const summary = createImportSummary(dryRunResult);
    //
    //   setImportResult({ ...dryRunResult, summary, file });
    //   setShowImportPreview(true);
    //
    // } catch (error) {
    //
    //   showAlert(`Import failed: ${error.message}`);
    // } finally {
    //   setImporting(false);
    // }
  };

  // Phase 2.6: Confirm import temporarily disabled - will restore in Phase 3
  const handleConfirmImport = async (targetVesselId = null) => {
    console.warn('Phase 2.6: Backup import disabled - will return in Phase 3');
    showAlert('Backup import temporarily unavailable during migration');
    return;

    // if (!importResult) return;
    //
    // const confirmed = await showConfirm(
    //   `Import ${importResult.summary.totals.creates + importResult.summary.totals.updates} records? ` +
    //   `This will ${importResult.summary.totals.creates} create and ${importResult.summary.totals.updates} update records.`
    // );
    //
    // if (!confirmed) return;
    //
    // setImporting(true);
    // try {
    //   const finalResult = await importBackup(
    //     importResult.file,
    //     collections,
    //     { dryRun: false, targetVesselId }
    //   );
    //
    //   const summary = createImportSummary(finalResult);
    //
    //   // Log import
    //   await logImport(collections, finalResult, {
    //     userId: 'current_user', // TODO: Get from auth context
    //     orgId: org?.id || 'default_org'
    //   });
    //
    //   showAlert(
    //     `Import complete: ${summary.totals.creates} created, ${summary.totals.updates} updated, ${summary.totals.skips} skipped`
    //   );
    //
    //   setShowImportPreview(false);
    //   setImportResult(null);
    //
    // } catch (error) {
    //
    //   showAlert(`Import failed: ${error.message}`);
    // } finally {
    //   setImporting(false);
    // }
  };

  const handleLinkFolder = async () => {
    try {
      const directoryHandle = await requestBackupDirectory();
      setLinkedFolder(directoryHandle);
      showAlert('Backup folder linked successfully');
    } catch (error) {
      if (error.message !== 'User cancelled directory selection') {
        showAlert(`Failed to link folder: ${error.message}`);
      }
    }
  };

  // Phase 2.6: Auto-backup toggle temporarily disabled - will restore in Phase 3
  const handleToggleAutoBackup = async () => {
    console.warn('Phase 2.6: Auto-backup toggle disabled - will return in Phase 3');
    showAlert('Auto-backup settings temporarily unavailable during migration');
    return;

    // const newValue = !autoBackupEnabled;
    // setAutoBackupEnabled(newValue);
    //
    // await collections.upsert('meta', {
    //   id: 'backup_settings',
    //   vesselId: 'system',
    //   data: {
    //     autoBackupEnabled: newValue,
    //     lastBackup: lastBackup?.toISOString()
    //   },
    //   origin: 'backup_settings'
    //   });
    //
    // showAlert(`Auto-backup ${newValue ? 'enabled' : 'disabled'}`);
  };

  if (!hasFeature(FEATURES.ADVANCED_REPORTS)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Backup & Restore Not Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Advanced backup features require a Professional or Fleet plan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Backup & Restore</h3>
          <p className="mt-1 text-sm text-gray-500">
            Export data for backup or import from previous exports
          </p>
        </div>
        
        <div className="p-6">
          {/* Last Backup Status */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Calendar className="h-6 w-6 text-gray-400" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Last Backup</h4>
                  <p className="text-sm text-gray-500">
                    {lastBackup 
                      ? lastBackup.toLocaleString()
                      : 'Never backed up'
                    }
                  </p>
                </div>
              </div>
              
              {supportsFileSystemAccess() && (
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={autoBackupEnabled}
                      onChange={handleToggleAutoBackup}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Auto-backup nightly</span>
                  </label>
                  
                  <button
                    onClick={handleLinkFolder}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Folder className="h-4 w-4 mr-2" />
                    {linkedFolder ? 'Change Folder' : 'Link Folder'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Export Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a backup of your vessel data
          </p>
        </div>
        
        <div className="p-6">
          {/* Vessel Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Vessels to Export
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedVessels.length === vessels.length}
                  onChange={(e) => {
                    setSelectedVessels(e.target.checked ? vessels.map(v => v.id) : []);
                  }}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-900">
                  All Vessels ({vessels.length})
                </span>
              </label>
              
              {vessels.map(vessel => (
                <label key={vessel.id} className="flex items-center ml-6">
                  <input
                    type="checkbox"
                    checked={selectedVessels.includes(vessel.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedVessels([...selectedVessels, vessel.id]);
                      } else {
                        setSelectedVessels(selectedVessels.filter(id => id !== vessel.id));
                      }
                    }}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{vessel.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Export Actions */}
          <div className="flex space-x-4">
            <button
              onClick={() => handleExport(false)}
              disabled={exporting || selectedVessels.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Clock className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {exporting ? 'Exporting...' : 'Download Backup'}
            </button>
            
            {linkedFolder && (
              <button
                onClick={() => handleExport(true)}
                disabled={exporting || selectedVessels.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Folder className="h-4 w-4 mr-2" />
                Save to Linked Folder
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Import Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Restore data from a previous backup
          </p>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to select backup file</span>
                </p>
                <p className="text-xs text-gray-500">ZIP files only</p>
              </div>
              <input
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                disabled={importing}
                className="hidden"
              />
            </label>
          </div>
          
          {importing && (
            <div className="mt-4 flex items-center justify-center">
              <Clock className="h-5 w-5 text-cyan-600 animate-spin mr-2" />
              <span className="text-sm text-gray-600">Analyzing backup file...</span>
            </div>
          )}
        </div>
      </div>

      {/* Import Preview Modal */}
      {showImportPreview && importResult && (
        <ImportPreviewModal
          result={importResult}
          onConfirm={handleConfirmImport}
          onCancel={() => {
            setShowImportPreview(false);
            setImportResult(null);
          }}
          vessels={vessels}
        />
      )}
    </div>
  );
};

// Import Preview Modal Component
const ImportPreviewModal = ({ result, onConfirm, onCancel, vessels }) => {
  const [targetVesselId, setTargetVesselId] = useState('');
  
  const summary = result.summary;
  const isDifferentOrg = summary.manifest.org?.name !== result.currentOrg?.name;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onCancel} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Import Preview
              </h3>
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            
            {/* Backup Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Backup Information</h4>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Organization</dt>
                  <dd className="text-gray-900">{summary.manifest.org?.name || 'Unknown'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Exported</dt>
                  <dd className="text-gray-900">
                    {new Date(summary.manifest.exportedAt).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Vessels</dt>
                  <dd className="text-gray-900">{summary.vesselCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Version</dt>
                  <dd className="text-gray-900">{summary.manifest.version}</dd>
                </div>
              </dl>
              
              {summary.ageWarning && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                  ⚠️ {summary.ageWarning}
                </div>
              )}
            </div>

            {/* Import Summary */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Import Summary</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{summary.totals.creates}</div>
                  <div className="text-sm text-green-700">New Records</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{summary.totals.updates}</div>
                  <div className="text-sm text-blue-700">Updates</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">{summary.totals.skips}</div>
                  <div className="text-sm text-gray-700">Unchanged</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{summary.conflicts}</div>
                  <div className="text-sm text-red-700">Conflicts</div>
                </div>
              </div>
            </div>

            {/* Warnings */}
            {isDifferentOrg && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">
                      Different Organization
                    </h4>
                    <p className="text-sm text-yellow-700">
                      This backup is from "{summary.manifest.org?.name}". 
                      Verify this is the correct data before importing.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Vessel Remapping */}
            {summary.vesselCount === 1 && vessels.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Import to Different Vessel (Optional)
                </label>
                <select
                  value={targetVesselId}
                  onChange={(e) => setTargetVesselId(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="">Use original vessel ID</option>
                  {vessels.map(vessel => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.name} ({vessel.id})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(targetVesselId || null)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700"
              >
                Import Data
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupManager;